import { ReactNode, useCallback, useState } from 'react';
import classNames from 'classnames';

import { TOOLTIP_OFFSET } from '../../../../constants/tooltip.constants';
import { Popover, PopoverTheme, TooltipContentPosition } from '../Popover';

import styles from './Tooltip.module.css';

export interface TooltipTheme extends PopoverTheme {
  Content?: string;
}

export interface TooltipProps {
  dataHcName: string;
  trigger: ReactNode;
  label?: ReactNode;
  description?: ReactNode;
  isLargeTooltip?: boolean;
  position?: TooltipContentPosition;
  showOnMouseEnter?: boolean;
  theme?: TooltipTheme;
  active?: boolean;
}

export const Tooltip = ({
  dataHcName,
  trigger,
  label,
  description,
  isLargeTooltip,
  position,
  showOnMouseEnter = true,
  theme,
  active,
}: TooltipProps) => {
  if (!label && !description) {
    console.warn(`
    ⚠️ Attention ⚠️
    Component Tooltip is not rendering content.
    You must add either a label or description.
    `);
  }
  const [isContentOverflowed, setIsContentOverflowed] = useState(false);
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setIsContentOverflowed(node.scrollHeight > node.clientHeight);
    }
  }, []);

  const content = (
    <div
      ref={contentRef}
      data-hc-name={`${dataHcName}-popover-content`}
      className={classNames(styles.PopoverContent, theme?.Content, {
        [styles.textAlignLeft]: isLargeTooltip,
        [styles.noDescription]: isLargeTooltip && !description,
      })}
    >
      {label}
      {description && (
        <div
          data-hc-name={`${dataHcName}-popover-description`}
          className={classNames(styles.Description, {
            [styles.textAlignLeft]: isLargeTooltip,
          })}
        >
          {description}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      active={active}
      positionOffset={TOOLTIP_OFFSET}
      position={position}
      theme={{
        ...theme,
        Caret: classNames(styles.Caret, theme?.Caret),
      }}
      // Omit the caret only when the content is too big.
      renderCaret={!isContentOverflowed}
      showOnMouseEnter={showOnMouseEnter}
      trigger={trigger}
      content={content}
      dataHcName={dataHcName}
    />
  );
};
